import React, { useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Switch } from '@mui/material'

import { TableUrlPagination } from '../../../../ui'
import { eeiiIpDataPreparation } from '../../logics'
import { EEIIIP_TABLE_COLUMNS } from '../../constants/eeii-ip-table.constants'
import { useRowSelector } from '../../../loader/hooks'
import { TableActions } from '../table-actions'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const EEIIIPTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const navigate = useNavigate()
  const eeiiIpLoader = useSelector((state) => state.loader.eeiiIp)
  const combos = useSelector((state) => state.combos)

  const data = eeiiIpLoader?.data?.empresas || []
  const totalEntries = eeiiIpLoader?.data?.total_empresas
  const search = eeiiIpLoader?.config?.params
  const loading = eeiiIpLoader?.loading

  const { selection, addSelection } = useRowSelector({
    identifier: 'eeiiIp',
    selectFilter: (state) => state?.data?.empresas,
    idField: 'id',
  })

  useEffect(() => {
    if (selection.length) {
      addSelection([])
    }
  }, [])

  const handleGoToDetail = (e, row) => {
    navigate(`/eeii/${row.id}`)
  }

  const handleSelectRows = useCallback(
    (rows) => {
      addSelection(rows)
    },
    [addSelection]
  )

  const formattedColumns = useMemo(() => {
    return EEIIIP_TABLE_COLUMNS.map((column) => {
      const baseColumn = {
        accessorKey: column.field,
        header: formatMessage({ id: `pages.eeiiIp.table.columns.${column.field}.title` }),
        size: column.width,
        minSize: column.width,
        maxSize: column.width,
      }

      if (column.field === 'active') {
        return {
          ...baseColumn,
          header: formatMessage({ id: 'global.active' }),
          Cell: (props) => <TableActions {...props} />,
          muiTableBodyCellProps: { align: 'center' },
        }
      }

      if (column.comboId && combos[column.comboId]?.data) {
        const lookup = getLookup(combos[column.comboId].data)
        return {
          ...baseColumn,
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return <span>{lookup[value] ?? value ?? '--'}</span>
          },
        }
      }

      return {
        ...baseColumn,
        Cell: ({ cell }) => <span>{cell.getValue() ?? '--'}</span>,
      }
    })
  }, [combos, formatMessage])

  const processedData = useMemo(() => eeiiIpDataPreparation(data, { intl }), [data, intl])

  const finalTableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      fixedColumns: {
        right: 1,
      },
      tableLayout: 'fixed',
      draggable: false,
    }),
    []
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={finalTableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onSelectionChange={handleSelectRows}
      onRowClick={handleGoToDetail}
    />
  )
}
